// 排程工作（Quartz Job）API 服務模組
// 呼叫 mygenieap 的 /web/schedule/* 代理端點（mygenieap 內部再轉呼叫 mgn-quartz 的 /job/*）。
import apiService from './apiService';
import * as appStorage from '../utils/appIndexedDB';
import logger from '../utils/logger';

const TOKEN_STORAGE_KEY = 'mygenie_token';
const REFRESH_TOKEN_STORAGE_KEY = 'mygenie_refresh_token';

/**
 * 帶身分驗證呼叫 /web/schedule/*，業務碼 401（token 過期）時自動 refresh 並重送一次，
 * 沿用 apiService 既有的 refresh/重新登入流程（與 sendMessageExternal 等方法一致）。
 * 成功回傳 mgn-quartz 原始回應內容（{ code: "0000", data, message } 這一層，已經剝掉
 * mygenieap ApiResponse 的外層包裝）；失敗一律拋出 Error。
 */
async function authorizedRequest(path, { method = 'GET', body } = {}) {
  const accessToken = await appStorage.getItem(TOKEN_STORAGE_KEY);
  if (!accessToken) {
    throw new Error('尚未登入或 token 遺失，請重新登入');
  }

  const doCall = (token) =>
    apiService.request(path, {
      method,
      headers: { Authorization: `Bearer ${token}` },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

  // 手機閒置一段時間後連線可能已在背景死掉，下一次呼叫會逾時／AbortError，且完全沒有 HTTP
  // 回應可看（error.http 不存在，同 apiService.verifyToken/refreshToken 判斷網路層錯誤的方式）。
  // 這跟下面「業務碼 401 → refresh token」是不同成因：token 是好的，只是舊連線已經死了，
  // 換 token 沒用，重打一次讓瀏覽器建立新連線即可，故不重試有 HTTP 回應的情況（例如乾淨的
  // 400/500 業務錯誤，重試只會拿到一樣的結果）。
  const callWithRetry = async (token) => {
    try {
      return await doCall(token);
    } catch (error) {
      if (error.http) throw error;
      logger.warn('排程', '排程 API 網路層錯誤（逾時/斷線），重試一次', { path, error: error.message });
      return await doCall(token);
    }
  };

  let response = await callWithRetry(accessToken);

  if (response?.code === 401) {
    logger.warn('排程', '排程 API 命中業務碼 401，進入 refresh token 流程', { path });
    const refreshToken = await appStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    if (!refreshToken) {
      apiService.clearAuthAndRedirectToLogin();
      throw new Error('登入已過期，請重新登入');
    }
    const refreshResult = await apiService.refreshToken(refreshToken);
    if (!refreshResult.success) {
      apiService.showReauthRequiredDialog(() => apiService.clearAuthAndRedirectToLogin());
      throw new Error('登入已過期，請重新登入');
    }
    response = await callWithRetry(refreshResult.token);
  }

  if (response?.code !== 200) {
    const message = response?.message || '排程服務發生錯誤';
    logger.error('排程', '排程 API 呼叫失敗', { path, code: response?.code, message });
    throw new Error(message);
  }
  // mygenieap 的 ApiResponse.data 就是 mgn-quartz 原始回應（{code, data, message}）
  return response.data;
}

/**
 * 查詢排程列表（分頁），回傳 mgn-quartz queryjob 的原始資料：{ content: [...], page: {...} }。
 * 內容包含進行中／已暫停的排程，也包含已完成但尚未刪除的一次性排程（status 為 "COMPLETE"）——
 * 後端已經把兩者合併在同一份清單裡，前端不用另外查詢或切換畫面。
 */
export function listSchedules({ page, size } = {}) {
  const query = [];
  if (page !== undefined) query.push(`page=${encodeURIComponent(page)}`);
  if (size !== undefined) query.push(`size=${encodeURIComponent(size)}`);
  const qs = query.length ? `?${query.join('&')}` : '';
  return authorizedRequest(`/web/schedule${qs}`);
}

/**
 * 新增排程。jobGroupName（內部識別碼）由後端自動產生，不用（也不能）由前端指定；
 * 回應中的 data.jobGroupName 就是後端產生的識別碼，之後編輯/暫停/刪除/查歷史都要用這個值。
 */
export function createSchedule({ displayName, cronExpression, jobDescription, prompt }) {
  return authorizedRequest('/web/schedule', {
    method: 'POST',
    body: { displayName, cronExpression, jobDescription, prompt },
  });
}

/** 修改（重新排程）既有排程，displayName 可隨時更改，留空則沿用既有名稱不變。 */
export function updateSchedule(jobGroupName, { cronExpression, jobDescription, prompt, displayName }) {
  return authorizedRequest(`/web/schedule/${encodeURIComponent(jobGroupName)}`, {
    method: 'PUT',
    body: { cronExpression, jobDescription, prompt, displayName },
  });
}

/** 刪除排程（不可復原） */
export function deleteSchedule(jobGroupName) {
  return authorizedRequest(`/web/schedule/${encodeURIComponent(jobGroupName)}`, { method: 'DELETE' });
}

/** 暫停排程 */
export function pauseSchedule(jobGroupName) {
  return authorizedRequest(`/web/schedule/${encodeURIComponent(jobGroupName)}/pause`, { method: 'POST' });
}

/** 恢復已暫停的排程 */
export function resumeSchedule(jobGroupName) {
  return authorizedRequest(`/web/schedule/${encodeURIComponent(jobGroupName)}/resume`, { method: 'POST' });
}

/** 立即觸發執行一次（非同步送出，不等待 AI 執行完成） */
export function runScheduleNow(jobGroupName) {
  return authorizedRequest(`/web/schedule/${encodeURIComponent(jobGroupName)}/run`, { method: 'POST' });
}

/**
 * 查詢一個排程的執行歷史（分頁），回傳 { job_id, runs: [{run_id, status, created_at, finished_at, snippet}], total, offset, limit }。
 * 資料來源為 mgn-quartz 落地保存的執行紀錄（非即時代理 Relay），欄位形狀對齊 Relay history API。
 */
export function getScheduleHistory(jobGroupName, { offset, limit } = {}) {
  const query = [];
  if (offset !== undefined) query.push(`offset=${encodeURIComponent(offset)}`);
  if (limit !== undefined) query.push(`limit=${encodeURIComponent(limit)}`);
  const qs = query.length ? `?${query.join('&')}` : '';
  return authorizedRequest(`/web/schedule/${encodeURIComponent(jobGroupName)}/history${qs}`);
}

/** 查詢單筆執行的完整結果，回傳 { run_id, status, result: {text}, message, ... }（欄位形狀對齊 Relay queryRun API）。 */
export function getScheduleRun(jobGroupName, runId) {
  return authorizedRequest(
    `/web/schedule/${encodeURIComponent(jobGroupName)}/runs/${encodeURIComponent(runId)}`
  );
}
